import { useState, useMemo, useCallback } from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Text from '@zenbusiness/zui/Text';

import { CLICKSTREAM_COMMON_PROPS } from '#client/components/utils/constants';

import * as Styled from './ListPanel.style';

/**
 * Generic list panel component for displaying items with multi-select capabilities
 * Used for batch selection patterns (e.g., selecting slogans for export)
 *
 * @param {object} props - Component props
 * @param {Array} props.items - Array of items to display
 * @param {Function} [props.getItemId] - Function to extract ID from item (default: item => item.id)
 * @param {Set|Array} [props.selectedIds] - Currently selected item IDs
 * @param {Function} [props.onSelectionChange] - Handler for selection changes (itemId, isSelected) => void
 * @param {Function} props.renderItem - Render function for items (item, { checked, onCheckChange }) => ReactNode
 * @param {Function} [props.onExport] - Handler for export action
 * @param {string} [props.exportButtonText='Export selected'] - Text for export button
 * @param {string} [props.headerText] - Optional header text above list
 * @param {Function} [props.selectionCountFormat] - Format function for selection count (selected, total) => string
 * @param {boolean} [props.showBottomExportButton=false] - Show duplicate export button at bottom
 * @param {boolean} [props.loading=false] - Whether operations are in progress
 * @param {string} [props.clickstreamPrefix='list_panel'] - Prefix for clickstream element names
 * @param {string} [props.emptyMessage='No items to display'] - Message when items array is empty
 * @component
 */
function ListPanel({
  items,
  getItemId = (item) => item.id,
  selectedIds,
  onSelectionChange,
  renderItem,
  onExport,
  exportButtonText = 'Export selected',
  headerText,
  selectionCountFormat,
  showBottomExportButton = false,
  loading = false,
  clickstreamPrefix = 'list_panel',
  emptyMessage = 'No items to display'
}) {
  const [exportLoading, setExportLoading] = useState(false);

  // Convert selectedIds to Set for consistent .has() checking
  const selectedSet = useMemo(() => {
    if (!selectedIds) return new Set();
    if (selectedIds instanceof Set) return selectedIds;
    return new Set(selectedIds);
  }, [selectedIds]);

  const selectedCount = selectedSet.size;
  const totalCount = items?.length || 0;

  // Default selection count format
  const formatSelectionCount = useCallback(
    (selected, total) => {
      if (selectionCountFormat) {
        return selectionCountFormat(selected, total);
      }
      return `${selected} of ${total} selected for export`;
    },
    [selectionCountFormat]
  );

  const handleExport = useCallback(async () => {
    if (exportLoading || loading || !onExport) {
      return;
    }

    setExportLoading(true);
    try {
      await onExport();
    } catch (error) {
      console.error('Failed to export:', error);
    } finally {
      setExportLoading(false);
    }
  }, [exportLoading, loading, onExport]);

  const handleSelectionChange = useCallback(
    (itemId, isSelected) => {
      if (onSelectionChange) {
        onSelectionChange(itemId, isSelected);
      }
    },
    [onSelectionChange]
  );

  const showExport = Boolean(onExport);
  const isLoading = loading || exportLoading;

  // Empty state
  if (!items || items.length === 0) {
    return (
      <Styled.MainContent>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Text color="secondary">{emptyMessage}</Text>
        </Box>
      </Styled.MainContent>
    );
  }

  return (
    <Styled.MainContent>
      <Styled.ContentHeader>
        {headerText && (
          <Text size="md" weight="medium">
            {headerText}
          </Text>
        )}
        <Styled.HeaderRow>
          <Styled.SelectionCount aria-live="polite">
            {formatSelectionCount(selectedCount, totalCount)}
          </Styled.SelectionCount>
          {showExport && (
            <Button
              clickstreamProps={{
                ...CLICKSTREAM_COMMON_PROPS,
                elementName: `${clickstreamPrefix}_export`,
                elementText: exportButtonText
              }}
              color="primary"
              disabled={isLoading}
              loading={exportLoading}
              size="small"
              startIcon={<DownloadIcon />}
              variant="contained"
              onClick={handleExport}
            >
              {exportButtonText}
            </Button>
          )}
        </Styled.HeaderRow>
      </Styled.ContentHeader>

      <Styled.ContentBody>
        <Styled.ItemsList>
          {items.map((item) => {
            const id = getItemId(item);
            const checked = selectedSet.has(id);
            return renderItem(item, {
              checked,
              onCheckChange: (isChecked) => handleSelectionChange(id, isChecked)
            });
          })}
        </Styled.ItemsList>

        {showBottomExportButton && showExport && (
          <Styled.FooterSection>
            <Button
              clickstreamProps={{
                ...CLICKSTREAM_COMMON_PROPS,
                elementName: `${clickstreamPrefix}_export_bottom`,
                elementText: exportButtonText
              }}
              color="primary"
              disabled={isLoading}
              loading={exportLoading}
              size="medium"
              startIcon={<DownloadIcon />}
              variant="contained"
              onClick={handleExport}
            >
              {exportButtonText}
            </Button>
          </Styled.FooterSection>
        )}
      </Styled.ContentBody>
    </Styled.MainContent>
  );
}

ListPanel.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  getItemId: PropTypes.func,
  selectedIds: PropTypes.oneOfType([
    PropTypes.instanceOf(Set),
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
  ]),
  onSelectionChange: PropTypes.func,
  renderItem: PropTypes.func.isRequired,
  onExport: PropTypes.func,
  exportButtonText: PropTypes.string,
  headerText: PropTypes.string,
  selectionCountFormat: PropTypes.func,
  showBottomExportButton: PropTypes.bool,
  loading: PropTypes.bool,
  clickstreamPrefix: PropTypes.string,
  emptyMessage: PropTypes.string
};

export default ListPanel;
